import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { jsonFetch } from '@/lib/api/jsonFetch'
import type {
  SopDetail,
  SopEmailSentResponse,
  SopListQuery,
  SopListResponse,
  SopHeaderKey,
  SopRecipient,
  SopSectionKey,
  SopTemplate,
} from './types'

const BASE = '/api/sop-library'

function invalidateAll(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['sop-library'] })
}

export function useSops(query: SopListQuery) {
  const params = new URLSearchParams()
  if (query.q) params.set('q', query.q)
  if (query.category) params.set('category', query.category)
  if (query.tags?.length) params.set('tags', query.tags.join(','))
  if (query.status) params.set('status', query.status)
  params.set('limit', String(query.limit ?? 200))
  params.set('offset', String(query.offset ?? 0))

  return useQuery({
    queryKey: ['sop-library', 'list', query],
    queryFn: () => jsonFetch<SopListResponse>(`${BASE}?${params.toString()}`),
    staleTime: 15_000,
  })
}

export function useSopCategories() {
  return useQuery({
    queryKey: ['sop-library', 'categories'],
    queryFn: () => jsonFetch<{ categories: string[] }>(`${BASE}/categories`),
    staleTime: 5 * 60_000,
  })
}

export function useSopRecipients() {
  return useQuery({
    queryKey: ['sop-library', 'recipients'],
    queryFn: () => jsonFetch<{ recipients: SopRecipient[] }>(`${BASE}/recipients`),
    staleTime: 60_000,
  })
}

export function useSopTemplate() {
  return useQuery({
    queryKey: ['sop-library', 'template'],
    queryFn: () => jsonFetch<SopTemplate>(`${BASE}/template`),
    staleTime: 60 * 60_000,
  })
}

export function isReadingPdf(sop: Pick<SopDetail, 'extraction_state'> | undefined): boolean {
  return sop?.extraction_state === 'pending' || sop?.extraction_state === 'running'
}

export function useSop(id: string | null) {
  return useQuery({
    queryKey: ['sop-library', 'detail', id],
    queryFn: () => jsonFetch<SopDetail>(`${BASE}/${id}`),
    enabled: id !== null,
    // While the attached PDF is being read into the template, check every
    // 2 seconds so the sections appear as soon as they are written.
    refetchInterval: (query) => (isReadingPdf(query.state.data) ? 2_000 : false),
  })
}

export type SopHeaderValues = Partial<Record<SopHeaderKey, string | null>>

export interface CreateSopVars extends SopHeaderValues {
  title: string
  category: string
  tags: string[]
  /** Template content; omit to start from an empty template. */
  sections?: Partial<Record<SopSectionKey, string>>
}

export function useCreateSop() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: CreateSopVars) =>
      jsonFetch<SopDetail>(BASE, { method: 'POST', body: vars }),
    onSuccess: () => invalidateAll(qc),
  })
}

export interface ImportSopVars {
  file: File
  title: string
  category: string
  tags: string[]
}

export function useImportSop() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: ImportSopVars) => {
      const form = new FormData()
      form.append('file', vars.file)
      form.append('title', vars.title)
      form.append('category', vars.category)
      form.append('tags', vars.tags.join(','))
      return jsonFetch<SopDetail>(`${BASE}/import`, { method: 'POST', rawBody: form })
    },
    onSuccess: () => invalidateAll(qc),
  })
}

export interface UpdateSopVars extends SopHeaderValues {
  id: string
  title?: string
  category?: string
  tags?: string[]
  /** Merged per key on the server; send only the sections that changed. */
  sections?: Partial<Record<SopSectionKey, string>>
}

export function useUpdateSop() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...vars }: UpdateSopVars) =>
      jsonFetch<SopDetail>(`${BASE}/${id}`, { method: 'PATCH', body: vars }),
    onSuccess: () => invalidateAll(qc),
  })
}

export function useReplaceSopPdf() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { id: string; file: File }) => {
      const form = new FormData()
      form.append('file', vars.file)
      return jsonFetch<SopDetail>(`${BASE}/${vars.id}/replace-pdf`, {
        method: 'POST',
        rawBody: form,
      })
    },
    onSuccess: () => invalidateAll(qc),
  })
}

/** Read the attached PDF into the template: fill empty sections, or
 *  (`replace`) overwrite them with what the PDF says. */
export function useReadSopPdf() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { id: string; replace: boolean }) =>
      jsonFetch<SopDetail>(`${BASE}/${vars.id}/read-pdf`, { method: 'POST', body: { replace: vars.replace } }),
    onSuccess: () => invalidateAll(qc),
  })
}

export function usePublishSop() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => jsonFetch<SopDetail>(`${BASE}/${id}/publish`, { method: 'POST' }),
    onSuccess: () => invalidateAll(qc),
  })
}

export function useArchiveSop() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => jsonFetch<SopDetail>(`${BASE}/${id}/archive`, { method: 'POST' }),
    onSuccess: () => invalidateAll(qc),
  })
}

/** Email one or several published SOPs (approved by Chris 2026-10-13). */
export function useEmailSops() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { ids: string[]; recipients: string[]; note?: string }) =>
      jsonFetch<SopEmailSentResponse>(
        vars.ids.length === 1 ? `${BASE}/${vars.ids[0]}/email` : `${BASE}/email`,
        {
          method: 'POST',
          body: {
            ...(vars.ids.length === 1 ? {} : { sop_ids: vars.ids }),
            recipients: vars.recipients,
            note: vars.note ?? null,
          },
        },
      ),
    onSuccess: () => invalidateAll(qc),
  })
}
